import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, FileText, Loader2, Lock, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadDocument } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";

const ALLOWED = ["pdf", "jpg", "jpeg", "png", "webp", "doc", "docx", "xls", "xlsx", "csv", "txt"];
const MAX = 10 * 1024 * 1024;

export function DocumentsBox({ clientId, entityType = "cliente", entityId, testid = "documents" }) {
  const { user } = useAuth();
  const internalUser = user?.role !== "client";
  const params = { client_id: clientId, entity_type: entityType === "cliente" && !entityId ? undefined : entityType, entity_id: entityId };
  const { data, reload } = useApi(clientId || user?.role === "client" ? "/documents" : null, params);
  const [busy, setBusy] = useState(false);
  const [internal, setInternal] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const ref = useRef();

  const upload = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) return toast.error("Tipo de archivo no permitido");
    if (file.size > MAX) return toast.error("El archivo supera 10 MB");
    const fd = new FormData();
    fd.append("file", file);
    if (clientId) fd.append("client_id", clientId);
    fd.append("entity_type", entityType);
    if (entityId) fd.append("entity_id", entityId);
    fd.append("internal", internalUser && internal ? "true" : "false");
    setBusy(true);
    try {
      await api.post("/documents", fd);
      toast.success("Documento subido");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input ref={ref} type="file" className="hidden" accept={ALLOWED.map((e) => `.${e}`).join(",")} onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-file-input`} />
        <Button size="sm" variant="outline" onClick={() => ref.current.click()} disabled={busy} data-testid={`${testid}-upload-button`}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Subir documento
        </Button>
        {internalUser && (
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} className="accent-amber-600" data-testid={`${testid}-internal`} /> Solo interno
          </label>
        )}
        <span className="text-xs text-slate-400">PDF, imágenes, Word, Excel, CSV o TXT · máx. 10 MB</span>
      </div>
      <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
        {(data || []).length === 0 && <li className="p-4 text-sm text-slate-400">Sin documentos.</li>}
        {(data || []).map((d) => (
          <li key={d.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`${testid}-item-${d.id}`}>
            <FileText className="h-4 w-4 shrink-0 text-slate-400" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-slate-800">{d.original_filename}</p>
              <p className="text-xs text-slate-400">{d.uploaded_by_name} · {fmtDateTime(d.created_at)} · {(d.size / 1024).toFixed(0)} KB</p>
            </div>
            {d.internal && <Lock className="h-3.5 w-3.5 text-amber-600" title="Interno" />}
            <Button size="icon" variant="ghost" aria-label="Descargar" onClick={() => downloadDocument(d).catch((e) => toast.error(errMsg(e)))} data-testid={`${testid}-download-${d.id}`}><Download className="h-4 w-4" /></Button>
            {user?.role === "admin" && (
              <Button size="icon" variant="ghost" aria-label="Eliminar" onClick={() => setToDelete(d)} data-testid={`${testid}-delete-${d.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            )}
          </li>
        ))}
      </ul>
      <ConfirmDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)} destructive confirmLabel="Eliminar"
        title="¿Eliminar documento?" description={`Se eliminará «${toDelete?.original_filename}». La acción queda registrada en el historial.`}
        testid={`${testid}-delete-confirm`}
        onConfirm={async () => {
          try { await api.delete(`/documents/${toDelete.id}`); toast.success("Documento eliminado"); setToDelete(null); reload(); }
          catch (e) { toast.error(errMsg(e)); }
        }} />
    </div>
  );
}
